import pluralize from 'pluralize';

import { type ClipType } from '@air/api/types';

import {
  isWorkspaceAgentAnchorPosition,
  WORKSPACE_AGENT_ANCHOR_POSITIONS,
  type WorkspaceAgentAnchorPosition,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentAnchorPositions';
import {
  type WorkspaceAgentAssetMentionItem,
  type WorkspaceAgentContextSourceFieldValueMentionItem,
  type WorkspaceAgentMentionRowItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { getWorkspaceAgentGuidedVariantSkipKind } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentGuidedVariantSkipCounts';
import {
  hasWorkspaceAgentGuidedVariantChosenImageFormat,
  WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  type WorkspaceAgentGuidedVariantOutput,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';

export const WORKSPACE_AGENT_WATERMARK_LIMITS = {
  marginPercentMax: 100,
  opacityMax: 100,
  sizePercentMax: 100,
  sizePercentMin: 1,
} as const;

export type WorkspaceAgentWatermarkPlacementId = WorkspaceAgentAnchorPosition;

export type WorkspaceAgentWatermarkPlacement = {
  id: WorkspaceAgentWatermarkPlacementId;
  insetEdges: string | null;
  label: string;
  phrase: string;
};

type WorkspaceAgentWatermarkPlacementCopy = Omit<WorkspaceAgentWatermarkPlacement, 'id'>;

const WORKSPACE_AGENT_WATERMARK_PLACEMENT_COPY = {
  'bottom-center': {
    insetEdges: 'the bottom edge',
    label: 'Bottom center',
    phrase: 'on the bottom edge, horizontally centered',
  },
  'bottom-left': {
    insetEdges: 'the bottom and left edges',
    label: 'Bottom left',
    phrase: 'in the bottom-left corner',
  },
  'bottom-right': {
    insetEdges: 'the bottom and right edges',
    label: 'Bottom right',
    phrase: 'in the bottom-right corner',
  },
  center: { insetEdges: null, label: 'Center', phrase: 'in the center of the image' },
  'middle-left': {
    insetEdges: 'the left edge',
    label: 'Middle left',
    phrase: 'on the left edge, vertically centered',
  },
  'middle-right': {
    insetEdges: 'the right edge',
    label: 'Middle right',
    phrase: 'on the right edge, vertically centered',
  },
  'top-center': {
    insetEdges: 'the top edge',
    label: 'Top center',
    phrase: 'on the top edge, horizontally centered',
  },
  'top-left': { insetEdges: 'the top and left edges', label: 'Top left', phrase: 'in the top-left corner' },
  'top-right': { insetEdges: 'the top and right edges', label: 'Top right', phrase: 'in the top-right corner' },
} as const satisfies Record<WorkspaceAgentAnchorPosition, WorkspaceAgentWatermarkPlacementCopy>;

export const WORKSPACE_AGENT_WATERMARK_PLACEMENTS: readonly WorkspaceAgentWatermarkPlacement[] =
  WORKSPACE_AGENT_ANCHOR_POSITIONS.map((id) => ({
    id,
    ...WORKSPACE_AGENT_WATERMARK_PLACEMENT_COPY[id],
  }));

export type WorkspaceAgentWatermarkMarginUnit = 'percent' | 'px';

export type WorkspaceAgentWatermarkSettings = {
  margin: number;
  marginUnit: WorkspaceAgentWatermarkMarginUnit;
  opacity: number;
  placement: WorkspaceAgentWatermarkPlacementId;
  sizePercent: number;
};

export const WORKSPACE_AGENT_WATERMARK_DEFAULTS: WorkspaceAgentWatermarkSettings = {
  margin: 5,
  marginUnit: 'percent',
  opacity: WORKSPACE_AGENT_WATERMARK_LIMITS.opacityMax,
  placement: 'bottom-right',
  sizePercent: 20,
};

export const WORKSPACE_AGENT_WATERMARK_DEFAULT_RENAME_SUFFIX = 'watermarked';

export type WorkspaceAgentWatermarkImage =
  | WorkspaceAgentAssetMentionItem
  | WorkspaceAgentContextSourceFieldValueMentionItem;

/** Same image check rounded corners and remove-background use, including the extension so photo-typed PDFs stay files. */
export const isWorkspaceAgentWatermarkImageSubject = (subject: { ext?: string | null; type: ClipType }) =>
  getWorkspaceAgentGuidedVariantSkipKind(subject) === 'image';

export type WorkspaceAgentWatermarkSubject = {
  ext?: string | null;
  height: number | null;
  /** Watermark settings do not draw a subject preview, so this stays empty for the shared selection. */
  thumbnailUrl: null;
  type: ClipType;
  width: number | null;
};

export const isWorkspaceAgentWatermarkPlacementId = (value: string): value is WorkspaceAgentWatermarkPlacementId =>
  isWorkspaceAgentAnchorPosition(value);

export const isWorkspaceAgentWatermarkAssetMention = (
  item: WorkspaceAgentMentionRowItem,
): item is WorkspaceAgentAssetMentionItem => {
  if (item.kind !== 'asset') {
    return false;
  }

  return !!item.assetType && isWorkspaceAgentWatermarkImageSubject({ ext: item.ext, type: item.assetType });
};

export type WorkspaceAgentWatermarkImageDimensions = {
  height: number | null;
  width: number | null;
};

/**
 * A pixel inset is measured from the edges of each selected image. The shorter side of the
 * smallest one is the furthest inset that still lands on every image. Clips that have not
 * reported dimensions are skipped so a not-yet-loaded image does not collapse the cap to 0.
 */
export const getWorkspaceAgentWatermarkMarginPxMax = (
  images: readonly WorkspaceAgentWatermarkImageDimensions[],
): number | null => {
  let shortestSide: number | null = null;

  for (const image of images) {
    if (!image.width || !image.height) {
      continue;
    }

    const side = Math.min(image.width, image.height);

    if (shortestSide === null || side < shortestSide) {
      shortestSide = side;
    }
  }

  return shortestSide;
};

export type WorkspaceAgentWatermarkScopeAsset = {
  versionId: string;
};

export type WorkspaceAgentWatermarkScopeClip = {
  ext?: string | null;
  height: number | null;
  type: ClipType;
  width: number | null;
};

type ToWorkspaceAgentWatermarkSubjectsParams = {
  assets: readonly WorkspaceAgentWatermarkScopeAsset[];
  clipsByVersionId: ReadonlyMap<string, WorkspaceAgentWatermarkScopeClip>;
};

/**
 * Counts only assets that are actually in composer scope, and only once their clip is known.
 * Select-all keeps clips in Redux while scope pills are empty.
 */
export const toWorkspaceAgentWatermarkSubjects = ({
  assets,
  clipsByVersionId,
}: ToWorkspaceAgentWatermarkSubjectsParams): WorkspaceAgentWatermarkSubject[] =>
  assets.flatMap((asset) => {
    const clip = clipsByVersionId.get(asset.versionId);

    return clip ? [{ ext: clip.ext, height: clip.height, thumbnailUrl: null, type: clip.type, width: clip.width }] : [];
  });

export const getWorkspaceAgentWatermarkImageDimensions = (
  subjects: readonly WorkspaceAgentWatermarkSubject[],
): WorkspaceAgentWatermarkImageDimensions[] =>
  subjects.flatMap((subject) =>
    isWorkspaceAgentWatermarkImageSubject(subject) ? [{ height: subject.height, width: subject.width }] : [],
  );

const placementFor = (placementId: WorkspaceAgentWatermarkPlacementId) =>
  WORKSPACE_AGENT_WATERMARK_PLACEMENTS.find((placement) => placement.id === placementId) ??
  WORKSPACE_AGENT_WATERMARK_PLACEMENTS[WORKSPACE_AGENT_WATERMARK_PLACEMENTS.length - 1];

export type BuildWorkspaceAgentWatermarkPromptInput = {
  imageCount: number;
  output?: WorkspaceAgentGuidedVariantOutput;
  settings: WorkspaceAgentWatermarkSettings;
  skippedFileCount: number;
  skippedVideoCount: number;
};

/** The two text runs that sit on either side of the watermark image pill. */
export type WorkspaceAgentWatermarkPrompt = {
  afterImage: string;
  beforeImage: string;
};

export const buildWorkspaceAgentWatermarkPrompt = ({
  imageCount,
  output = WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  settings,
  skippedFileCount,
  skippedVideoCount,
}: BuildWorkspaceAgentWatermarkPromptInput): WorkspaceAgentWatermarkPrompt => {
  const placement = placementFor(settings.placement);
  const target = imageCount === 1 ? 'the selected image' : 'each selected image';
  const marginAmount =
    settings.marginUnit === 'px' ? `${settings.margin} pixels` : `${settings.margin}% of the base image width`;
  const margin = placement.insetEdges ? `Inset it by ${marginAmount} from ${placement.insetEdges}.` : null;
  const opacityFraction = settings.opacity / WORKSPACE_AGENT_WATERMARK_LIMITS.opacityMax;
  const opacityNumber = Number.isInteger(opacityFraction)
    ? String(opacityFraction)
    : opacityFraction.toFixed(2).replace(/0+$/, '');
  const opacityAtBound =
    settings.opacity <= 0
      ? 'Set the overlay opacity to 0 (fully transparent).'
      : 'Set the overlay opacity to 1 (fully opaque).';
  const opacity =
    settings.opacity > 0 && settings.opacity < WORKSPACE_AGENT_WATERMARK_LIMITS.opacityMax
      ? `Set the overlay opacity to ${opacityNumber} (${settings.opacity}%).`
      : opacityAtBound;
  const skip =
    skippedFileCount + skippedVideoCount > 0
      ? 'Skip videos and other non-image files — this watermark only applies to images.'
      : null;

  const afterImage = [
    `as a flat watermark on ${target}.`,
    `Resize the watermark first so its width is ${settings.sizePercent}% of the base image width and its aspect ratio stays the same. The overlay is placed at that resized pixel size, not at its original size.`,
    `Place the resized watermark ${placement.phrase}.`,
    margin,
    opacity,
    hasWorkspaceAgentGuidedVariantChosenImageFormat(output)
      ? 'Keep the base image at its original pixel dimensions, resolution, and aspect ratio — do not upscale, downscale, or crop.'
      : 'Keep the base image at its original pixel dimensions, resolution, and aspect ratio — do not upscale, downscale, crop, or re-encode.',
    'Paste it as a flat overlay. Do not redraw, recolor, or blend the watermark into the picture.',
    skip,
  ]
    .filter((part): part is string => !!part)
    .join(' ');

  return { afterImage: ` ${afterImage}`, beforeImage: 'Apply ' };
};

export type BuildWorkspaceAgentWatermarkDescriptionInput = {
  settings: WorkspaceAgentWatermarkSettings;
  skippedFileCount: number;
  skippedVideoCount: number;
};

export const buildWorkspaceAgentWatermarkDescription = ({
  settings,
  skippedFileCount,
  skippedVideoCount,
}: BuildWorkspaceAgentWatermarkDescriptionInput): string => {
  const placement = placementFor(settings.placement);
  const summary = [
    placement.label,
    `${settings.sizePercent}% size`,
    `${settings.opacity}% opacity`,
    settings.placement === 'center' ? null : `${settings.margin}${settings.marginUnit === 'px' ? 'px' : '%'} margin`,
  ]
    .filter((part): part is string => !!part)
    .join(' · ');

  const skippedCount = skippedFileCount + skippedVideoCount;

  if (skippedCount < 1) {
    return summary;
  }

  const noun = skippedFileCount > 0 ? 'file' : 'video';

  return `${summary}. ${skippedCount.toLocaleString()} ${pluralize(noun, skippedCount)} skipped.`;
};
